import { Entity } from '@common/types/Entity';
import { PrimitiveType } from '@common/types/interimTypes';
import { CopilotFixType, Diagnostic } from '@LSP/types';
import { NodeOperationType } from '@prophecy/ui-v3/Graph/types';

import { CopilotAgentFeedback, SQLCopilotAgentStatus } from './CopilotAgentChat/types';
import {
  ChatWidgetType,
  CopilotPipelineUpdateMode,
  type WidgetType,
  type StepType,
  type LegacyAIDatasetInfo,
  type BaseChatWidget,
  type TaskItem,
  type TableWidgetType,
  type UserMarkdownWidgetType,
  CopilotQuestionPromptType,
  CopilotOnboardingDatasetDetailType,
  ChartWidgetType
} from './CopilotAgentChat/widgetTypes';
import { CopilotDescribeType } from './copilotAtomTypes';
import { FullInterimPortData } from './copilotInterimTypes';

export enum CopilotAgents {
  Kinesis = 'kinesis',
  DataExplorer = 'dataExplorer'
}

export enum CopilotResponseTypes {
  CopilotExpressionsResponse = 'CopilotExpressionsResponse',
  CopilotRequestDiscarded = 'CopilotRequestDiscarded',
  CopilotSpecFlowResponse = 'CopilotSpecFlowResponse',
  CopilotSpecFlowChatHistoryResponse = 'CopilotSpecFlowChatHistoryResponse'
}

export enum CopilotKinesisResponseTypes {
  TransformationWidget = 'TransformationWidget',
  AddNewDatasetsWidget = 'AddNewDatasetsWidget',
  CopilotKinesisGraphDifferenceResponse = 'CopilotKinesisGraphDifferenceResponse',
  QuestionWidget = 'QuestionWidget',
  MarkdownWidget = 'MarkdownWidget'
}

export type CopilotExpressionSuggestion = {
  expression: string;
  confidence: number;
  description: string;
  targetName?: string;
  propertyPath: string;
};

export type CopilotGemFixes = {
  changes: { propertyPath: string; value: unknown }[];
  summary?: string;
};

/** Metadata for Data Tests copilot expression generation */
export type DataTestMetadata = {
  testType?: string;
  fieldName?: string;
  testMetadata?: Record<string, unknown>;
};

export type CopilotExpressionParams = {
  propertyPath: string;
  processId: string;
  targetColumn?: string;
  interimData?: {
    [key: string]: {
      data?: unknown[];
      stats?: unknown[];
    };
  };
  userPrompt: string | null;
  dataTestsMetadata?: DataTestMetadata;
};

export enum CopilotGraphAdditionType {
  AddGraphRequest = 'AddGraphRequest',
  AudioAddGraphRequest = 'AudioAddGraphRequest'
}

export type CopilotGraphParams = {
  userPrompt?: string;
  audioPrompt?: string;
  additionType: CopilotGraphAdditionType;
};

export type PromptMessage = { isSuccess: boolean; userPrompt: string; assistantResponseSummary: string };
export type CopilotModificationParams =
  | {
      modificationType: CopilotModificationType.AddGem;
      processId: string;
      portId: string;
      userPrompt: string;
    }
  | {
      modificationType: CopilotModificationType.ConversationModification;
      previousMessages: PromptMessage[];
      userPrompt: string;
      selectedProcessLabels: string[];
    }
  | {
      modificationType: CopilotModificationType.AudioAddGemRequest;
      processId: string;
      portId: string;
      userPrompt: string;
      audioPrompt?: string;
    }
  | {
      modificationType: CopilotModificationType.AudioConversationModificationRequest;
      previousMessages: PromptMessage[];
      userPrompt: string;
      audioPrompt?: string;
      selectedProcessLabels: string[];
    };

export enum CopilotTriggerType {
  user = 'user',
  auto = 'auto'
}

type CopilotDescribeGemParams = {
  processIds: string[];
  type: CopilotDescribeType.CopilotDescribeGemRequest;
  triggerType?: CopilotTriggerType;
};

type CopilotDescribeDataSourceParams = {
  propertyPath: string;
  type: CopilotDescribeType.CopilotDescribeDataSourceRequest;
  triggerType?: CopilotTriggerType;
};

export type CopilotDescribeColumnParams = {
  columnNames: string[];
  propertyPath: string;
  type: CopilotDescribeType.CopilotDescribeColumnRequest;
  triggerType?: CopilotTriggerType;
  interimData?: { data: PrimitiveType[][] };
};

export type CopilotDescribeParams =
  | CopilotDescribeGemParams
  | CopilotDescribeDataSourceParams
  | CopilotDescribeColumnParams;

export type CopilotGetLabelParams = {
  processIds: string[];
  triggerType?: CopilotTriggerType;
};

export type CopilotGetScripParams = {
  processId: string;
  userPrompt: string;
  triggerType?: CopilotTriggerType;
};

export type CopilotGetUdfParams = {
  udfName: string;
  userPrompt: string;
  triggerType?: CopilotTriggerType;
};

export enum MacroPredictionType {
  MacroSqlDefinitionRequest = 'MacroSqlDefinitionRequest',
  MacroSqlFullRequest = 'MacroSqlFullRequest'
}

export enum GenericDataTestPredictionType {
  GenericDataTestSqlDefinitionRequest = 'GenericDataTestSqlDefinitionRequest',
  GenericDataTestSqlFullRequest = 'GenericDataTestSqlFullRequest'
}

export type CopilotGetMacroParams = {
  userPrompt: string;
  predictionType: MacroPredictionType | GenericDataTestPredictionType;
  triggerType?: CopilotTriggerType;
};

export type CopilotFixParams = {
  copilotFixType: CopilotFixType;
  diagnostics: Diagnostic[];
};

export type CopilotExplanation = {
  summary?: string;
  processDescriptions: Record<string, string>;
  processIds: string[];
  readOnly?: boolean;
};

export type GraphDifference = {
  processId?: string;
  operation: NodeOperationType;
  updatedColumnCount?: number;
  cteLabel?: string;
};

export enum CopilotAnimationState {
  start = 'start',
  animating = 'animating',
  completed = 'completed'
}

export enum CopilotModificationType {
  AddGem = 'AddGemRequest',
  AudioAddGemRequest = 'AudioAddGemRequest',
  ConversationModification = 'ConversationModificationRequest',
  AudioConversationModificationRequest = 'AudioConversationModificationRequest'
}

export type CopilotModificationFilter = {
  selectedProcesses: { processId: string; portId?: string; processLabel: string }[];
  modificationType: CopilotModificationType;
};

export enum CopilotChatUserType {
  copilot = 'copilot',
  user = 'user',
  replay = 'replay',
  status = 'status'
}

export type ChatType = {
  enableFormFilling?: boolean;
  questionId?: string;
  message?: string;
  isSuccess?: boolean;
  canAcceptReject?: boolean;
  userType: `${CopilotChatUserType}`;
  editable: boolean;
  mode?: string;
  id: string;
  widgets?: ChatWidget[];
  steps?: StepType[];
  time?: number;
  isMarkdown?: boolean;
  refreshTables?: boolean;
  type?: CopilotKinesisResponseTypes | string;
  messageId?: string;
  title?: string;
  questionTemplate?: React.ReactNode;
  gitCommitMessage?: string;
  userFeedback?: CopilotAgentFeedback;
};

export type CopilotAgentChat = {
  tabId: string;
  tabHeader: string | React.ReactNode;
  chats?: ChatType[];
  currentPrompt?: string;
  chatPipelineUpdateMode?: CopilotPipelineUpdateMode;
  pinnedNodeIds?: string[];
};

export enum ContextOption {
  Auto = 'auto',
  Append = 'append',
  Edit = 'edit'
}

export enum CopilotPreviewStatus {
  Open = 'Open',
  Loading = 'Loading',
  Close = 'Close'
}

export type CopilotState = {
  isPopoverOpen?: boolean;
  isEditingPrompt?: boolean;
  suggestionActive?: boolean;
  suggestionLoaded?: boolean;
  animationState?: CopilotAnimationState;
  graphDifference?: { [modelOrPipelineName: string]: GraphDifference[] };
  showChanges?: boolean;
  modificationFilter?: CopilotModificationFilter;
  explanation?: CopilotExplanation;
  currentPrompt?: string;
  currentAudioPrompt?: string;
  gemFixes?: {
    fixes: CopilotGemFixes;
    appliedFixIndex: number;
    processId: string;
    processPath: string;
  };
  autoUpdatingComment?: Record<string, boolean>;
  chats?: Record<string, ChatType[]>;
  // Agent state
  isAgentChatOpen?: boolean;
  isDocumentReadOnly?: boolean;
  isAgentMakingChanges?: boolean;
  agentChats?: CopilotAgentChat[];
  selectedAgentChatTabId?: string;
  chatHistory?: ChatType[];
  status?: SQLCopilotAgentStatus;
  agentStatusBarVisible?: boolean;
  isFetchingMarkdown?: boolean;
  fetchingMarkdownFilePath?: string;
  previewingStatus?: CopilotPreviewStatus;
  restoreInProgressMessageId?: string;
  previewInProgressMessageId?: string;
  transformationInProgress?: boolean;
  activeTabIds?: string[];
  isInspectModeEnabled?: boolean;
  currentInspectingProcessId?: string;
  switchedToEditModeDueToErrors?: boolean;
  currentQuestionId?: string;
};

export type CopilotAgentState = {};

export type CopilotSuggestPromptParams = {
  previousMessages: PromptMessage[];
};

export type CopilotActionOptions = {
  saveInHistory?: boolean;
  historyMergeId?: string;
  replace?: boolean;
  prevValue?: CopilotState;
  nextValue?: Partial<CopilotState>;
};

export type SuggestGemPropertiesParams = {
  processId: string;
  interimData: {
    [portSlug: string]: Pick<FullInterimPortData, 'data' | 'stats'>;
  };
};

/* Business Rule start */
export enum BusinessRuleRequestType {
  businessRule = 'businessRule',
  businessRuleContext = 'businessRuleContext'
}

export type BusinessRuleRow = {
  inputs: Record<string, string>;
  outputs: Record<string, string>;
  description?: string;
};

export type BusinessRuleRequest = {
  name: string;
  userPrompt: string;
  contextKey: string;
  inputs: Array<{
    parameter_name: string;
    parameter_type: string;
    description?: string;
  }>;
  outputs: string[];
  contextToRowMap: Record<string, BusinessRuleRow[]>;
};

export type BusinessRuleResponse = Omit<BusinessRuleRequest, 'name' | 'userPrompt' | 'contextKey'> & {
  summary: string;
};

export type BREConversation = {
  message: string;
  isPrompt: boolean;
  timestamp: number;
};

export type BusinessRuleChatHistoryRequest = {
  businessRuleName: string;
  contextKey?: string;
  newContextKey?: string;
};

export type BusinessRuleChatHistoryResponse = {
  conversations: Record<string, Array<BREConversation>>;
};
/* Business Rule end */

export enum CopilotKinesisChatRequestType {
  TextRequest = 'CopilotKinesisTextRequest',
  EntityGraphDifference = 'CopilotKinesisGraphDifferenceRequest',
  RestoreRequest = 'CopilotKinesisRestoreRequest',
  CancelRequest = 'CopilotKinesisCancelRequest',
  PreviewRequest = 'CopilotKinesisPreviewRequest',
  FeedbackRequest = 'CopilotKinesisFeedbackRequest',
  AdditionalFeedbackRequest = 'CopilotKinesisAdditionalFeedbackRequest'
}

export type SessionInfo = {
  sessionId: string;
  chatId: string;
  hostname: string;
  commit?: string;
  timestamp: number;
  version: string;
};

export type CopilotUploadParams = {
  uploadId: string;
  context?: CopilotAgentContext;
  conversationId: string;
  uploadedAt: number;
  triggerAnalysis?: boolean;
  analysisIntent?: string;
  files?: { name: string; path: string; fileType?: 'Table' | 'File' }[];
};

export type CopilotChatParams =
  | {
      type: CopilotKinesisChatRequestType.TextRequest;
      message: string;
      context?: CopilotAgentContext;
      mentions?: Record<string, CopilotAgentContextValue>;
      chatId: string;
      mode?: string;
      messageId: string;
      pipelineUpdateMode: CopilotPipelineUpdateMode;
      entityType?: Entity;
      ideViewType?: string;
      entityFilePath?: string;
      executionSessionId?: string;
      isFabricConnected?: boolean;
      /** @default true */
      persist?: boolean;
      /** @default true */
      transformEnable?: boolean;
    }
  | {
      type: CopilotKinesisChatRequestType.EntityGraphDifference;
      entity: string;
      chatId: string;
    }
  | {
      type: CopilotKinesisChatRequestType.RestoreRequest;
      chatId: string;
      messageId: string;
    }
  | {
      type: CopilotKinesisChatRequestType.CancelRequest;
      chatId: string;
    }
  | {
      type: CopilotKinesisChatRequestType.PreviewRequest;
      chatId: string;
      messageId: string;
    }
  | {
      type: CopilotKinesisChatRequestType.FeedbackRequest;
      chatId: string;
      messageId: string;
      rating: CopilotAgentFeedback;
      prophecyVersion: string;
    }
  | {
      type: CopilotKinesisChatRequestType.AdditionalFeedbackRequest;
      feedbackId: string;
      feedbackClass: string;
      feedbackTitle: string;
      feedbackMessage?: string;
      screenshotData?: string;
      sharePrompts: boolean;
      chatId: string;
      messageId: string;
      fullStoryUrl?: string;
      sessionInfo: SessionInfo;
      userEmail: string;
    };

type LSPMethodResponseType<T> = {
  data: T;
  error?: { message: string };
};

export type CopilotSpecFlowResponse =
  | LSPMethodResponseType<{
      type: CopilotResponseTypes.CopilotSpecFlowResponse;
      message?: { text: string };
      widgets?: ChatWidget[];
    }>
  | LSPMethodResponseType<{
      type: CopilotResponseTypes.CopilotSpecFlowResponse;
      result: GraphDifference[];
    }>;

type CopilotKinesisGraphDifferenceResponse = LSPMethodResponseType<{
  type: CopilotKinesisResponseTypes.CopilotKinesisGraphDifferenceResponse;
  result: GraphDifference[];
}>;

export enum CopilotKinesisOperationType {
  DatasetModification = 'datasetModification',
  Visualization = 'visualization',
  EntityModification = 'entityModification',
  CompareDatasets = 'compareDatasets',
  FinalizePipeline = 'finalizePipeline',
  Harmonization = 'harmonization',
  Unknown = 'unknown'
}

export enum CopilotKinesisOperationStatus {
  Success = 'success',
  Error = 'error'
}

type CopilotKinesisChatResponse = LSPMethodResponseType<{
  type: CopilotKinesisResponseTypes.TransformationWidget | CopilotKinesisResponseTypes.AddNewDatasetsWidget | string;
  message?: { text: string; isMarkdown?: boolean };
  messageId?: string;
  widgets?: ChatWidget[];
  refreshTables?: boolean;
  gitCommitMessage?: string;
  title?: string;
  openSpotSelectionView?: boolean;
  datasets?: {
    databaseName: string;
    schemaName: string;
    tableName: string;
  }[];
  operationStatus: CopilotKinesisOperationStatus;
  operationType: CopilotKinesisOperationType;
  enableFormFilling?: boolean;
  questionId?: string;
  aiEnhancedAnswer?: string;
}>;

export type CopilotKinesisResponse = CopilotKinesisChatResponse | CopilotKinesisGraphDifferenceResponse;

export function isCopilotKinesisGraphDifferenceResponse(
  response: CopilotKinesisResponse
): response is CopilotKinesisGraphDifferenceResponse {
  return response.data.type === CopilotKinesisResponseTypes.CopilotKinesisGraphDifferenceResponse;
}

export function isCopilotKinesisChatResponse(response: CopilotKinesisResponse): response is CopilotKinesisChatResponse {
  return response.data.type !== CopilotKinesisResponseTypes.CopilotKinesisGraphDifferenceResponse;
}

export type OnFixWithAiSuccessCb = (graphPath?: string, processPath?: string) => void;

export type GenerateExpressionSuggestionsResponse = {
  data: { type: CopilotResponseTypes; result: CopilotExpressionSuggestion[] };
};

export type CopilotChatResponseType = {
  role: CopilotChatUserType;
  content: {
    text: string;
    isMarkdown?: boolean;
  };
  timestamp: number;
  type: string;
  widgets?: ChatWidget[];
  messageId?: string;
  title?: string;
  refreshTables?: boolean;
  enableFormFilling?: boolean;
  questionId?: string;
};

export type ChatHistoryResponse = {
  chat?: Array<CopilotChatResponseType>;
};

export type CopilotAgentContextValue<T = unknown> = {
  kind: Entity;
  properties?: T;
};

export type CopilotAgentEntityContextValue = CopilotAgentContextValue<
  Partial<LegacyAIDatasetInfo> | { projectId: string; commitId?: string; branchId?: string } | Record<string, never>
>;

export type CopilotUploadedFileContext = Record<`@file:${string}`, CopilotAgentContextValue<{ path: string }>>;

export type CopilotAgentContext =
  | (Record<`@${string}`, CopilotAgentContextValue> & {
      pipelineUpdateMode: {
        kind: CopilotPipelineUpdateMode;
        properties: {
          scopeGems: string[];
        };
      };
    })
  | Record<string, CopilotAgentContextValue>;

export const DEFAULT_ENTITY_ID_FOR_CHAT = 'default';

export type CopilotAgentFlowOnFeedbackCallback = ({
  tabId,
  chat,
  agent,
  feedbackType
}: {
  tabId: string;
  chat: ChatType;
  agent: CopilotAgents;
  feedbackType: CopilotAgentFeedback;
}) => Promise<{ feedbackId: string } | undefined>;

export type CopilotChatMetainfoTypeV4 = {
  startTime?: number;
  endTime?: number;
  thought?: boolean;
};

// ThinkingGroupWidgetType - defined inline to handle circular reference with CopilotChatTypeV4
export type ThinkingGroupWidgetType = WidgetType<
  ChatWidgetType.ThinkingGroup,
  {
    thinkingTime?: number;
    children?: { message: CopilotChatTypeV4; collapsed?: boolean }[];
    messageIds: string[];
    collapsed?: boolean;
  }
>;

// Full ChatWidget union including ThinkingGroupWidgetType
export type ChatWidget = BaseChatWidget | ThinkingGroupWidgetType;

export type CopilotChatTypeV4 = ChatWidget & {
  id: string;
  role: CopilotChatUserType;
  metainfo: CopilotChatMetainfoTypeV4;
};

export type CopilotUserMessageTypeV4 = UserMarkdownWidgetType & {
  id: string;
  role: CopilotChatUserType.user;
  metainfo: CopilotChatMetainfoTypeV4;
};

export enum BackendCopilotAgentWidgetTypes {
  Chart = 'Chart',
  AgentThinking = 'assistant_thinking',
  AgentMessage = 'assistant_message',
  FinalMessage = 'final_message',
  Table = 'table',
  FileDiff = 'agent_edits_file',
  ToolUse = 'tool_use',
  Task = 'task',
  QuestionPrompt = 'QuestionPrompt',
  OnboardingDataset = 'OnboardingDataset',
  Compaction = 'compaction'
}

export type BackendCopilotAgentCompactionsWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.Compaction,
  {
    isComplete: boolean;
  }
>;

export type BackendCopilotAgentThinkingWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.AgentThinking,
  {
    content: string;
  }
>;

export type BackendCopilotAgentMessageWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.AgentMessage,
  {
    content: string;
    final_usage?: object;
  }
>;

/** Sentinel message from the agent marking the end of a turn; not shown in the chat body. */
/** Server may include e.g. `title` on `final_message` even though the UI does not render this widget. */
export type BackendCopilotFinalMessageWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.FinalMessage,
  { title?: string }
>;

export type BackendCopilotTableWidget = WidgetType<BackendCopilotAgentWidgetTypes.Table, TableWidgetType['properties']>;

export type BackendCopilotFileDiffWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.FileDiff,
  {
    filename: string;
    before_edit_state: string;
    lines_added: number;
    lines_removed: number;
    after_edit_state: string;
    headerTitle?: string;
    inProgress?: boolean;
  }
>;

export type BackendCopilotQuestionWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.QuestionPrompt,
  CopilotQuestionPromptType
>;

export type BackendCopilotOnboardingDatasetWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.OnboardingDataset,
  CopilotOnboardingDatasetDetailType
>;

export type BackendCopilotToolUseWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.ToolUse,
  {
    request: string;
    response: string;
    toolTitle?: string;
  }
>;

export type BackendCopilotTaskWidget = WidgetType<
  BackendCopilotAgentWidgetTypes.Task,
  {
    title?: string;
    description?: string;
    tasks: TaskItem[];
  }
>;

export type BackendCopilotChartWidget = WidgetType<BackendCopilotAgentWidgetTypes.Chart, ChartWidgetType['properties']>;

export type BackendCopilotAgentWidget =
  | BackendCopilotAgentThinkingWidget
  | BackendCopilotAgentMessageWidget
  | BackendCopilotFinalMessageWidget
  | BackendCopilotFileDiffWidget
  | BackendCopilotToolUseWidget
  | BackendCopilotTableWidget
  | BackendCopilotQuestionWidget
  | BackendCopilotOnboardingDatasetWidget
  | BackendCopilotTaskWidget
  | BackendCopilotChartWidget
  | BackendCopilotAgentCompactionsWidget;

export enum BackendCopilotAgentMessageUserTypes {
  Assistant = 'assistant',
  User = 'user',
  System = 'system'
}

export type BackendChatTypeV4 = {
  id: string;
  role: BackendCopilotAgentMessageUserTypes;
  metainfo: CopilotChatMetainfoTypeV4;
} & (BackendCopilotAgentWidget | ChatWidget);

export enum CopilotAgentEventTypesV4 {
  AgentStatus = 'AgentStatus',
  UndoAvailable = 'UndoAvailable',
  RedirectToEntity = 'RedirectToEntity',
  HighlightElements = 'HighlightElements',
  NextPromptSuggestion = 'NextPromptSuggestion'
}

export type HighlightElementOperation = 'highlight' | 'clear';
export type HighlightElementEntityType = 'MarkdownDocument' | 'Pipeline';
export type HighlightElementTargetKind = 'Question' | string;

export type HighlightElementTarget = {
  operation: HighlightElementOperation;
  entityType: HighlightElementEntityType;
  filePath: string;
  targetId: string;
  targetKind: HighlightElementTargetKind;
  properties?: {
    showCopilotIcon?: boolean;
  };
};

export type HighlightElementsActions = {
  clearAllPrevious?: boolean;
  scrollIntoView?: {
    targetId: string;
    targetKind: HighlightElementTargetKind;
    filePath?: string;
  };
};

export type HighlightElementsPayload = {
  type: CopilotAgentEventTypesV4.HighlightElements;
  actions?: HighlightElementsActions;
  targets: HighlightElementTarget[];
};

export enum CopilotAgentStatusV4 {
  Idle = 'idle',
  Working = 'working',
  Success = 'success',
  Error = 'error'
}

export type CopilotAgentEventV4 =
  | {
      type: CopilotAgentEventTypesV4.AgentStatus;
      status: CopilotAgentStatusV4;
    }
  | {
      type: CopilotAgentEventTypesV4.UndoAvailable;
      userMessageId: string;
      conversationId: string;
      available: boolean;
    }
  | {
      type: CopilotAgentEventTypesV4.RedirectToEntity;
      filePath: string;
    }
  | HighlightElementsPayload
  | {
      type: CopilotAgentEventTypesV4.NextPromptSuggestion;
      suggestions: string[];
      conversationId: string;
    };
